
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { sendReferralWelcomeEmail } from '@/utils/referralService';
import { ReferralUser } from '@/types/userRole';

const ActivateReferralPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isActivating, setIsActivating] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referralUser, setReferralUser] = useState<ReferralUser | null>(null);
  
  const token = searchParams.get('token');
  const referralId = searchParams.get('referralId');
  
  useEffect(() => {
    if (!token || !referralId) {
      setError('Invalid activation link. Missing token or referral ID.');
    } else {
      // In a real app, you would verify the token and referral ID against the database
      // For demo purposes, we'll just simulate this process
      
      // Simulate fetching the user associated with this token
      const fetchReferralUser = async () => {
        try {
          // Simulating API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Mock user data
          const mockUser: ReferralUser = {
            id: `user-${Date.now()}`,
            name: 'John Doe',
            email: 'john.doe@example.com',
            role: 'user',
            referralCode: referralId,
            activationStatus: 'pending',
            activationToken: token
          };
          
          setReferralUser(mockUser);
        } catch (error) {
          console.error('Error fetching referral user:', error);
          setError('Failed to fetch user details. Please try again later.');
        }
      };
      
      fetchReferralUser();
    }
  }, [token, referralId]);
  
  const handleActivate = async () => {
    if (!referralUser) return;
    
    setIsActivating(true);
    try {
      // In a real app, you would make an API call to activate the user's account
      // For demo purposes, we'll just simulate this process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update the user's status to active
      const activatedUser: ReferralUser = {
        ...referralUser,
        activationStatus: 'active'
      };
      
      // Send welcome email with referral code and QR code
      await sendReferralWelcomeEmail({
        name: activatedUser.name,
        email: activatedUser.email,
        referralCode: activatedUser.referralCode
      });
      
      setReferralUser(activatedUser);
      setIsActivated(true);
      
      toast({
        title: "Account Activated",
        description: "Your referral account has been successfully activated. Check your email for your referral code and QR code.",
      });
    } catch (error) {
      console.error('Error activating account:', error);
      setError('Failed to activate your account. Please try again later.');
      
      toast({
        title: "Activation Failed",
        description: "There was a problem activating your account. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsActivating(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="mt-2 text-3xl font-bold text-gray-900">Activate Your Account</h1>
          <p className="mt-2 text-gray-600">Complete your registration by activating your referral account.</p>
        </div>
        
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold">Referral Account Activation</CardTitle>
            <CardDescription>Verify your details and activate your account</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {error ? (
              <div className="bg-red-50 p-4 rounded-lg border border-red-100 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-red-500 mr-3 mt-1" />
                  <div>
                    <p className="text-red-800 font-medium">Activation Error</p>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            ) : isActivated ? (
              <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-6">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1" />
                  <div>
                    <p className="text-green-800 font-medium">Account Activated!</p>
                    <p className="text-green-700 text-sm mt-1">
                      Your referral account has been successfully activated. We've sent you an email with your unique referral code and QR code.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {referralUser ? (
                  <>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
                          {referralUser.name}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
                          {referralUser.email}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Referral ID</label>
                        <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
                          {referralUser.referralCode}
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full" 
                      onClick={handleActivate}
                      disabled={isActivating}
                    >
                      {isActivating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Activating...
                        </>
                      ) : (
                        'Activate Account'
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
            )}
            
            <div className="mt-6">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                {isActivated ? 'Proceed to Login' : 'Back to Login'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivateReferralPage;
